import React, { useMemo } from 'react';

import DateDropDown from '../common/datedropdown';
import { BaseDropDown } from '../common/dropdowns';
import SearchBar from '../common/SearchBar';
import DownloadCSVButton from '../common/downloadCSVButton';
import ActionButton from '../../common/ActionButton';
import history from '../../../history';
import {
    TFilterOptions,
    TPageSelectionState,
    TRebooksExtendingType,
    PAGE_SELECTION_STATE,
    REBOOKS_EXTENDING_TYPE,
} from './types';
import { mapDateParamsForApi } from './utils';

const ALL_BOOKINGS_OPTION = { label: 'All bookings', value: '' };
const ALL_AVAILABILITY_OPTION = { label: 'All availability', value: '' };

type TRebooksFiltersProps = {
    onFilterChange: (id: string, value: any) => void;
    currentUrlParams: URLSearchParams;
    filterOptions: TFilterOptions | null;
    selectedCount: number;
    pageSelectionState: TPageSelectionState;
    selectableOnPageCount: number;
    extendingType: TRebooksExtendingType;
    isRangeExtendable: boolean;
    onSelectAllOnPage: () => void;
    onDeselectAll: () => void;
    onExtendAll: () => void;
    onExtendSelected: () => void;
};

export const RebooksFilters = ({
    onFilterChange,
    currentUrlParams,
    filterOptions,
    selectedCount,
    pageSelectionState,
    selectableOnPageCount,
    extendingType,
    isRangeExtendable,
    onSelectAllOnPage,
    onDeselectAll,
    onExtendAll,
    onExtendSelected,
}: TRebooksFiltersProps) => {
    const isExtending = extendingType !== null;
    const isSeriesOptions = useMemo(
        () => [
            ALL_BOOKINGS_OPTION,
            ...(filterOptions?.is_series || []).map(opt => ({
                label: opt.label,
                value: String(opt.value),
            })),
        ],
        [filterOptions?.is_series],
    );

    const availabilityOptions = useMemo(
        () => [
            ALL_AVAILABILITY_OPTION,
            ...(filterOptions?.availability || []).map(opt => ({
                label: opt.label,
                value: String(opt.value),
            })),
        ],
        [filterOptions?.availability],
    );

    const csvParams = mapDateParamsForApi(currentUrlParams);

    const isExtendSelectedDisabled = selectedCount === 0 || isExtending;
    const isExtendAllDisabled = isExtending || !isRangeExtendable;

    let extendSelectedTooltip: string | undefined;
    if (isExtendSelectedDisabled) {
        extendSelectedTooltip = 'Extend the selected rebooks';
    }

    let extendAllTooltip: string | undefined;
    if (isExtendAllDisabled) {
        if (!isRangeExtendable) {
            extendAllTooltip =
                'Only the current week and up to 2 weeks back can be extended';
        } else {
            extendAllTooltip =
                'Extend all rebooks matching the current filters';
        }
    }

    return (
        <div className="table-filter-container">
            <DateDropDown
                onFilterChange={onFilterChange}
                history={history}
                hideOptions={true}
            />
            <BaseDropDown
                filterKey="is_series"
                selected={currentUrlParams.get('is_series') || ''}
                options={isSeriesOptions}
                defaultSelection={ALL_BOOKINGS_OPTION}
                onFilterChange={onFilterChange}
            />
            <BaseDropDown
                filterKey="availability"
                selected={currentUrlParams.get('availability') || ''}
                options={availabilityOptions}
                defaultSelection={ALL_AVAILABILITY_OPTION}
                onFilterChange={onFilterChange}
            />
            <SearchBar
                filterKey="search"
                customPlaceHolder="Search..."
                onFilterChange={onFilterChange}
                currentUrlParams={currentUrlParams}
            />
            <div className="table-action-bar">
                <div className="bookings-bulk-actions rebooks-bulk-actions--filters">
                    <div className="bookings-bulk-actions__details rebooks-bulk-actions__details">
                        {selectedCount > 0 && (
                            <span className="rebooks-selection-count">
                                {selectedCount} selected
                            </span>
                        )}
                        <div
                            className={`action-string ${
                                selectableOnPageCount === 0 ||
                                pageSelectionState === PAGE_SELECTION_STATE.ALL
                                    ? 'rebooks-action-string--disabled'
                                    : ''
                            }`}
                            onClick={() => {
                                if (
                                    selectableOnPageCount === 0 ||
                                    pageSelectionState === PAGE_SELECTION_STATE.ALL
                                ) {
                                    return;
                                }
                                onSelectAllOnPage();
                            }}
                            title={
                                selectableOnPageCount === 0
                                    ? 'No selectable rebooks on this page'
                                    : 'Select all rebooks on this page'
                            }
                        >
                            Select all on the page
                        </div>
                        <div
                            className={`action-string ${
                                selectedCount === 0
                                    ? 'rebooks-action-string--disabled'
                                    : ''
                            }`}
                            onClick={() => {
                                if (selectedCount === 0) {
                                    return;
                                }
                                onDeselectAll();
                            }}
                            title="Deselect all selected rebooks"
                        >
                            Deselect all
                        </div>
                    </div>
                </div>
                <DownloadCSVButton endpoint="rebooks" params={csvParams} />
                <div className="grouped-buttons">
                    <ActionButton
                        onAction={onExtendSelected}
                        text={
                            selectedCount > 0
                                ? `Extend selected (${selectedCount})`
                                : 'Extend selected'
                        }
                        tooltip={extendSelectedTooltip}
                        iconClass="icon-calendar-refresh"
                        disabled={isExtendSelectedDisabled}
                        loading={extendingType === REBOOKS_EXTENDING_TYPE.SELECTED}
                    />
                    <ActionButton
                        onAction={onExtendAll}
                        text="Extend all"
                        tooltip={extendAllTooltip}
                        iconClass="icon-calendar-refresh"
                        disabled={isExtendAllDisabled}
                        loading={extendingType === REBOOKS_EXTENDING_TYPE.ALL}
                    />
                </div>
            </div>
        </div>
    );
};
